// Shared shapes for every Admin Panel tab — see backend/routes/{users,roles,
// specialties,ailment-templates,ai-assistant-profiles,patient-merges,
// branches,ip (wards/beds),doctor-availability}.js.

import type { MatrixColumn, MatrixRow, PictorialOption } from '@/lib/assessmentTemplate';

export interface Role {
  id: number;
  name: string;
  modules: string[];
  is_system: boolean;
  active_user_count: number;
  /** Users of this role must use two-step login (G20); asked to set it up at next sign-in. */
  mfa_required?: boolean;
}

export interface AdminUser {
  id: number;
  username: string;
  full_name: string;
  role: string;
  specialty_id: number | null;
  active: boolean;
  created_at: string;
  /** Two-step login is on for this account (G20). */
  mfa_enabled?: boolean;
  // Signatory identity (docs/HMS_V4_IP_CASE_SHEET_INDIA_DESIGN.md §6.10) —
  // required before this user can sign an IP assessment or progress note.
  registration_no?: string | null;
  registration_council?: string | null;
  qualification?: string | null;
  designation?: string | null;
  hpr_id?: string | null;
}

export interface UserBranchGrant {
  id: number;
  code: string;
  name: string;
  is_default?: boolean;
}

export interface UserBranchesResponse {
  granted: UserBranchGrant[];
  available: UserBranchGrant[];
}

export interface Specialty {
  id: number;
  name: string;
  active: boolean;
}

export interface AilmentField {
  type: string;
  label: string;
  key: string;
  // A plain string list for checkbox-group/radio/vitals; a `PictorialOption[]`
  // for pictorial-choice (same `options` key, per
  // docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §3 — the stored answer is
  // still a plain string, so a template can switch between the two types
  // with zero data migration).
  options?: string[] | PictorialOption[];
  suffix?: string;
  history_source?: string;
  visible_if?: { mode: 'all' | 'any'; conditions: RuleCondition[] } | null;
  option_colors?: Record<string, string>;
  thresholds?: Array<{ max?: number; color: string }>;
  // `interval_months` only means anything for due-checklist items;
  // condition-status items (a plain label per condition, e.g. "Retinopathy")
  // leave it unset.
  items?: Array<{ label: string; interval_months?: number }>;
  contributes_to_summary?: boolean;
  summary_label?: string;
  // `choice-matrix` only — see docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §2.
  // Shapes match `lib/assessmentTemplate.ts`'s `MatrixColumn`/`MatrixRow`
  // exactly (this IS the authored JSON, unnormalised).
  columns?: MatrixColumn[];
  rows?: MatrixRow[];
  // `choice-matrix` only — one summary tile PER ROW instead of one for the
  // whole field. See assessmentScoring.js's `buildSummary`.
  summary_rows?: boolean;
  // Rendered as an info strip under the field, e.g. the diet matrix's
  // pass-level footnote.
  footnote?: string;
}

export interface AilmentSection {
  label: string;
  fields: AilmentField[];
  // A hint that this section should be wider (2 columns) in Pre-Consult's
  // `three-up` grid — see docs/HMS_V4_ASSESSMENT_FIELD_TYPES_SPEC.md §4.
  // Anything other than 1 or 2 clamps to 1 at normalise time.
  layout?: { span?: 1 | 2 };
}

export interface RuleCondition {
  field: string;
  op: string;
  value: string | string[];
}

export interface ScoringRule {
  mode: 'all' | 'any';
  conditions: RuleCondition[];
  result: string;
}

/** The four Pre-Consult "Triage" fields — see TriageFieldsEditor.tsx. */
export type TriageFieldKey = 'risk_level' | 'pain_scale' | 'allergies' | 'current_meds';
export const ALL_TRIAGE_FIELD_KEYS: TriageFieldKey[] = ['risk_level', 'pain_scale', 'allergies', 'current_meds'];

export interface AilmentTemplate {
  id?: number;
  name: string;
  module_key: string | null;
  department: string | null;
  active?: boolean;
  fields_config: { sections: AilmentSection[] };
  default_fields_config?: { sections: AilmentSection[] } | null;
  scoring_rules: ScoringRule[] | null;
  // NULL means "all four" — see migration_ailment_triage_fields.sql. Same
  // maker-checker shape as fields_config: `triage_fields` is what's live,
  // `pending_triage_fields` a submitted-but-unapproved change, and
  // `default_triage_fields` the snapshot "Reset to default" restores.
  triage_fields?: TriageFieldKey[] | null;
  pending_triage_fields?: TriageFieldKey[] | null;
  default_triage_fields?: TriageFieldKey[] | null;
  pending_status?: 'pending' | 'rejected' | null;
  pending_submitted_by?: string;
  pending_submitted_at?: string;
  pending_reviewed_by?: string;
  pending_review_notes?: string;
}

export interface AIAssistantProfileSummary {
  id?: number;
  name: string;
  description: string | null;
  doc_type: string;
  active?: boolean;
  pending_status?: 'pending' | 'rejected' | null;
  pending_action?: 'activate' | 'edit';
  pending_submitted_by?: string;
  pending_submitted_at?: string;
  pending_reviewed_by?: string;
  pending_review_notes?: string;
  pending_content?: string;
}

export interface AIAssistantProfile extends AIAssistantProfileSummary {
  content: string;
}

export interface MergeRequest {
  id: number;
  loser_name: string;
  loser_uhid: string;
  survivor_name: string;
  survivor_uhid: string;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  requested_by: string;
  requested_at: string;
  review_notes?: string;
  merge_summary?: { moved: Record<string, number> };
}

export interface Branch {
  id: number;
  code: string;
  name: string;
  address: string | null;
  phone: string | null;
  gstin: string | null;
  active: boolean;
  is_default: boolean;
}

export interface Bed {
  id: number;
  bed_number: string;
  status: 'available' | 'occupied' | 'maintenance';
}

export interface Ward {
  id: number;
  name: string;
  ward_type: string;
  daily_rate: number;
  floor: string | null;
  active: boolean;
  beds: Bed[];
}

export interface Doctor {
  id: number;
  full_name: string;
}

export interface QueueSettings {
  default_slot_minutes: number;
  delay_notify_threshold_minutes: number;
  booking_cutoff_minutes: number;
  emergency_handling_mode: 'manual' | 'automatic';
  availability_lock_minutes: number;
  late_checkin_grace_minutes: number;
  // IANA zone (e.g. "Asia/Kolkata") the naive date+start_time columns this
  // app stores are interpreted in when compared against the current time —
  // see migration_queue_settings_timezone.sql.
  timezone: string;
  // Window the Dashboard's "Patient wait time breakdown" card computes over —
  // see migration_wait_time_breakdown_window.sql and reports.js's WINDOW_DAYS.
  wait_time_breakdown_window: 'today' | '7d' | '30d' | '6m';
}

export interface BillingSettings {
  id: number;
  invoice_numbering_mode: 'shared' | 'per_branch';
  gst_enabled: boolean;
  gst_percentage_options: number[];
  updated_by: string | null;
  updated_at: string;
}

export interface DoctorBreak {
  id: number;
  start_time: string;
  end_time: string;
  label: string | null;
}

export interface DoctorAvailabilityDay {
  id: number;
  date: string;
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
  status: 'draft' | 'published' | 'locked' | 'completed';
  breaks: DoctorBreak[];
}

export interface AffectedPatient {
  id: number;
  type: 'slot' | 'queue';
  patient_name: string;
  patient_mobile: string;
  start_time?: string;
}

export interface AvailabilityException {
  id: number;
  date: string;
  exception_start: string;
  exception_end: string;
  reason: string;
  affected: AffectedPatient[];
}

export interface ReportUnavailabilityResult {
  mode: 'manual' | 'automatic';
  auto_reassigned: unknown[];
}

/**
 * Pharmacy loyalty programme rules (routes/loyalty.js). Off by default —
 * `enabled: false` means the counter shows no points panel at all, rather
 * than a panel showing zeroes.
 */
export interface LoyaltySettings {
  enabled: boolean;
  /** "Every ₹<earn_amount> spent earns <earn_points> point(s)." */
  earn_amount: number;
  earn_points: number;
  /** What one point takes off a later bill, in rupees. */
  point_value: number;
  eligible_amount_basis: 'subtotal' | 'after_discount' | 'final_total';
  max_redeem_percent: number;
  /** null = no rupee ceiling per bill. */
  max_redeem_amount: number | null;
  min_balance_to_redeem: number;
  /** 0 = points never expire. */
  expiry_months: number;
  updated_by?: string | null;
  updated_at?: string;
}

/**
 * A partial update. `max_redeem_amount: null` explicitly CLEARS the ceiling,
 * while omitting the key leaves it alone — the two have to stay
 * distinguishable or a ceiling could never be removed once set.
 */
export type LoyaltySettingsInput = Partial<Omit<LoyaltySettings, 'updated_by' | 'updated_at'>>;

/**
 * One customer holding pharmacy loyalty points.
 *
 * `points_available` is derived live from the open batches rather than read
 * from the cached account balance: expiry is evaluated per-patient ON READ
 * (see backend/utils/loyalty.js), and sweeping every member just to render a
 * list would be hundreds of writes per page load. So a point that has lapsed
 * but not yet been swept never shows here as spendable.
 */
export interface LoyaltyMember {
  patient_id: number;
  uhid: string;
  name: string;
  mobile: string;
  registration_source: 'visit' | 'pharmacy';
  lifetime_earned: number;
  lifetime_redeemed: number;
  lifetime_expired: number;
  last_activity_at: string | null;
  points_available: number;
  /** Of the available points, how many lapse within 60 days. */
  points_expiring_soon: number;
  next_expiry: string | null;
}

export interface LoyaltyMembersResponse {
  enabled: boolean;
  point_value: number;
  /**
   * The WHOLE programme, never just the rows a search returned — the points
   * a hospital owes is a finance figure, and one that moved when somebody
   * typed in a search box would be worse than no figure at all.
   */
  summary: { members: number; points_outstanding: number; value_outstanding: number };
  members: LoyaltyMember[];
}

export type LoyaltyEntryType = 'earn' | 'redeem' | 'expire' | 'adjust' | 'return_reversal' | 'return_refund';

/** One row of the points ledger — the "bank statement" view for a member. */
export interface LoyaltyLedgerRow {
  id: number;
  entry_type: LoyaltyEntryType;
  /** Signed: positive adds, negative removes. The balance is their sum. */
  points: number;
  points_remaining: number | null;
  amount_basis: string | null;
  rupee_value: string | null;
  expires_at: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string;
  bill_number: string | null;
  return_number: string | null;
}

// Drug Master normalization review queue — see
// docs/HMS_v4_Drug_Master_Formulary_Architecture.md §11-§12.
// confidence_tier/match_confidence are reviewer guidance only, never shown
// to a prescribing doctor (see the architecture doc's UX Requirements) —
// this tab is the one place they're surfaced.
export interface DrugReviewMedicine {
  id: number;
  generic_name: string;
  brand_name: string | null;
  strength: string | null;
  dosage_form: string | null;
  schedule: string;
  active: boolean;
  manufacturer: string | null;
}

export interface DrugReviewProposal {
  id: number;
  medicine_ids: number[];
  proposed_canonical_name: string;
  proposed_generic_name: string;
  proposed_strength: string | null;
  proposed_dosage_form: string | null;
  proposed_route: string | null;
  proposed_release_form: string | null;
  proposed_medicine_type: 'single' | 'fdc';
  match_confidence: string | null;
  confidence_tier: 'high' | 'medium' | 'low';
  match_basis: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'edited';
  resolved_drug_id: number | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewer_notes: string | null;
  medicines: DrugReviewMedicine[];
}

// Drug Master direct view/edit (POC) — see routes/drug-master.js. Bypasses
// drug_normalization_review on purpose; DrugReviewProposal above is unaffected.
export interface DrugMasterRow {
  id: number;
  canonical_name: string;
  generic_name: string;
  strength: string | null;
  strength_value: string | null;
  strength_unit: string | null;
  dosage_form: string | null;
  route: string | null;
  release_form: string | null;
  composition: string | null;
  medicine_type: 'single' | 'fdc';
  active: boolean;
  source: string;
  created_at: string;
  updated_at: string;
  /** Only present on GET /:id — how many medicines already point at this drug. */
  linked_medicine_count?: number;
}

export type NewDrugMasterInput = Partial<{
  canonical_name: string; generic_name: string; strength: string; dosage_form: string;
  route: string; release_form: string; composition: string; medicine_type: 'single' | 'fdc'; active: boolean;
}>;

export interface DrugMasterBulkUploadResult {
  processed: number;
  created: number;
  updated: number;
  failed: number;
  results: Array<{ row: number; status: 'created' | 'updated' | 'error'; id?: number; message?: string }>;
  uploaded_by: string;
}

// Hospital Formulary — see
// docs/HMS_v4_Drug_Master_Formulary_Architecture.md §5.6-§5.7, §13-§15.
// status is the ONLY source of truth for "recommended" (status='preferred')
// — there is deliberately no separate recommended boolean, so it can never
// disagree with status.
export interface FormularyProduct {
  id: number;
  formulary_id: number;
  product_id: number;
  status: 'preferred' | 'approved' | 'restricted' | 'not_recommended';
  priority: number;
  allow_substitution: boolean;
  active: boolean;
  notes: string | null;
  generic_name: string;
  brand_name: string | null;
  strength: string | null;
  dosage_form: string | null;
  manufacturer?: string | null;
}

export interface FormularyEntry {
  id: number;
  drug_id: number;
  status: 'preferred' | 'allowed' | 'restricted' | 'non_formulary';
  prescribing_restriction: string | null;
  default_product_id: number | null;
  allow_substitution: boolean;
  notes: string | null;
  active: boolean;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_reason: string | null;
  effective_from: string | null;
  effective_to: string | null;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
  canonical_name: string;
  generic_name: string;
  strength: string | null;
  dosage_form: string | null;
  product_count?: number;
  products?: FormularyProduct[];
}

export interface FormularyBulkUploadResult {
  processed: number;
  created: number;
  updated: number;
  failed: number;
  newProducts: number;
  results: Array<{ row: number | null; sheet: 'Formulary' | 'Formulary Products'; status: 'created' | 'updated' | 'error'; id?: number; message?: string }>;
  uploaded_by: string;
}

export interface UnconfiguredDrug {
  id: number;
  canonical_name: string;
  generic_name: string;
  strength: string | null;
  dosage_form: string | null;
}

// Insurance Claims — the payer master and the per-branch claim identity
// profile (docs/HMS_V4_INSURANCE_CLAIMS_DESIGN.md §4.1/§4.2).
export interface Payer {
  id: number;
  payer_type: 'insurer' | 'tpa' | 'govt_scheme' | 'corporate' | 'psu' | 'other';
  code: string;
  name: string;
  scheme_code: string | null;
  irdai_reg_no: string | null;
  portal_url: string | null;
  claims_email: string | null;
  phone: string | null;
  address: string | null;
  is_network: boolean;
  mou_valid_from: string | null;
  mou_valid_to: string | null;
  credit_days: number | null;
  submission_days: number | null;
  mou_discount_percent: number;
  tds_percent: number;
  /** Payer portal's per-file upload limit in MB; claim packets over it are split. Null = no limit. */
  max_upload_mb?: number | string | null;
  requires_preauth: boolean;
  default_document_profile: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BranchClaimProfile {
  branch_id: number;
  legal_name: string | null;
  rohini_id: string | null;
  registration_no: string | null;
  registration_state_code: string | null;
  pan: string | null;
  nabh_status: string | null;
  nabl_status: string | null;
  inpatient_beds: number | null;
  has_ot: boolean | null;
  has_icu: boolean | null;
  other_facilities: string | null;
  claims_email: string | null;
  claims_phone: string | null;
  authorised_signatory_user_id: number | null;
  seal_document_id: number | null;
  seal_storage_key?: string | null;
  seal_uploaded_at?: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

// The chargeable-service master (docs/HMS_V4_INSURANCE_CLAIMS_DESIGN.md §4.3,
// Insurance Claims Phase 2.1) — what a claim head/rate is picked FROM when an
// IP charge is added. Tenant-wide, like Payer.
export const CLAIM_HEADS = [
  'room', 'nursing', 'icu', 'ot', 'professional',
  'investigation_lab', 'investigation_imaging', 'investigation_other',
  'pharmacy', 'consumables', 'implants', 'procedure_misc', 'package',
  'ambulance', 'misc', 'non_payable',
] as const;
export type ClaimHead = typeof CLAIM_HEADS[number];

export interface ServiceMasterItem {
  id: number;
  code: string;
  name: string;
  department: string | null;
  claim_head: ClaimHead;
  unit: 'each' | 'day' | 'hour' | 'visit' | 'session';
  default_rate: number | string;
  gst_percent: number | string;
  sac_hsn: string | null;
  non_payable_default: boolean;
  auto_daily_for_ward_types: string[] | null;
  active: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
}

// Insurance Claims Phase 5.4 (routes/payer-document-rules.js, wireframe S9's
// 4th tab) — the document-checklist rules a claim's checklist is seeded
// from (backend/utils/claimDocuments.js#ensureClaimDocuments).
export interface PayerDocumentRule {
  id: number;
  profile: string;
  payer_id: number | null;
  package_id: number | null;
  stage: 'preauth' | 'final' | 'claim';
  doc_key: string;
  label: string;
  required: boolean;
  sort_order: number;
  auto_source: string | null;
  notes: string | null;
  active: boolean;
}

// Insurance Claims Phase 6.1 (routes/payer-tariffs.js, routes/packages.js,
// wireframe S9's 3rd tab "Payer tariffs & packages") — behind the
// `claims.payer_tariffs` flag (billing_settings.claims_payer_tariffs_enabled).
export interface PayerTariffsSettings {
  claims_payer_tariffs_enabled: boolean;
  claims_proportionate_heads: string[];
}

export interface PayerTariff {
  id: number;
  payer_id: number | null;
  service_id: number;
  room_category: string | null;
  rate: number | string;
  effective_from: string;
  effective_to: string | null;
  service_code?: string;
  service_name?: string;
  claim_head?: ClaimHead;
  payer_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface PackageItem {
  id: number;
  package_id: number;
  service_id: number;
  qty_included: number | string;
  service_code?: string;
  service_name?: string;
}

export interface Package {
  id: number;
  payer_id: number | null;
  payer_name?: string | null;
  code: string;
  name: string;
  specialty: string | null;
  scheme_package_code: string | null;
  room_category: string | null;
  los_days: number | null;
  rate: number | string;
  includes_heads: ClaimHead[];
  excludes_heads: ClaimHead[];
  icu_days_included: number | null;
  implant_included: boolean;
  mandatory_docs: Array<{ doc_key: string; label: string; required?: boolean; sort_order?: number }> | null;
  active: boolean;
  effective_from: string;
  effective_to: string | null;
  items?: PackageItem[];
  created_at: string;
  updated_at: string;
}

export interface PackageBulkImportResult {
  processed: number;
  created: number;
  updated: number;
  failed: number;
  results: Array<{ row: number; code: string | null; status: 'created' | 'updated' | 'error'; message?: string; id?: number }>;
}

export interface AdmissionPackage {
  id: number;
  admission_id: number;
  claim_id: number | null;
  package_id: number;
  applied_at: string;
  applied_by_user_id: number | null;
  rate_snapshot: number | string;
  los_days_snapshot: number | null;
  status: 'active' | 'removed';
  code: string;
  name: string;
  package_los_days: number | null;
  package_rate: number | string;
  includes_heads: ClaimHead[];
  excludes_heads: ClaimHead[];
  icu_days_included: number | null;
  implant_included: boolean;
}
